import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { TOKEN_KEY, ADMIN_TOKEN_KEY } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [cooldowns, setCooldowns] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionNotice, setSubscriptionNotice] = useState(null);

  const isAuthed = !!user;

  // ===== Bootstrap =====
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setSubscriptionNotice(data.user?.subscription_activation_notice || null);
      return data.user;
    } catch (e) {
      setUser(null);
      setSubscriptionNotice(null);
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await api.get('/accounts');
      setAccounts(data.accounts || []);
    } catch (e) {
      setAccounts([]);
    }
  }, []);

  const fetchCooldowns = useCallback(async () => {
    try {
      const { data } = await api.get('/cooldowns');
      const now = Date.now();
      setCooldowns((data.cooldowns || [])
        .map(c => ({
          id: c.id,
          removedAt: new Date(c.removed_at).getTime(),
          expiresAt: new Date(c.expires_at).getTime(),
        }))
        .filter(c => Number.isFinite(c.removedAt) && Number.isFinite(c.expiresAt) && c.expiresAt > now)
      );
    } catch (e) {
      setCooldowns([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      setIsAdmin(!!adminToken);
      if (token) {
        const u = await fetchMe();
        if (u) {
          await Promise.all([fetchAccounts(), fetchCooldowns()]);
        }
      }
      setLoading(false);
    })();
  }, [fetchMe, fetchAccounts, fetchCooldowns]);

  useEffect(() => {
    const refreshOnFocus = async () => {
      if (!localStorage.getItem(TOKEN_KEY)) return;
      const u = await fetchMe();
      if (u) {
        await Promise.all([fetchAccounts(), fetchCooldowns()]);
      }
    };
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [fetchMe, fetchAccounts, fetchCooldowns]);

  // ===== Actions =====
  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptionNotice(data.user?.subscription_activation_notice || null);
    await Promise.all([fetchAccounts(), fetchCooldowns()]);
    return data.user;
  };

  const register = async (email, username, password) => {
    const { data } = await api.post('/auth/register', { email, username, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setSubscriptionNotice(null);
    setAccounts([]);
    setCooldowns([]);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setSubscriptionNotice(null);
    setAccounts([]);
    setCooldowns([]);
  };

  const adminLoginStart = async (email, password) => {
    const { data } = await api.post('/admin/login', { email, password });
    return data; // { otp_session_id, dev_otp? }
  };

  const adminVerifyOtp = async (otp_session_id, otp) => {
    const { data } = await api.post('/admin/verify-otp', { otp_session_id, otp });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.admin_token);
    setIsAdmin(true);
    return true;
  };

  const adminLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAdmin(false);
  };

  const addAccount = async (access_url) => {
    const { data } = await api.post('/accounts', { access_url });
    setAccounts((cur) => [data.summary, ...cur]);
    return data; // includes account, summary, data_source, live_error
  };

  const bulkRemoveAccounts = async (ids) => {
    // We fire all deletes in parallel
    await Promise.all(ids.map(id => api.delete(`/accounts/${id}`)));
    setAccounts((cur) => cur.filter((a) => !ids.includes(a.id)));
    await fetchCooldowns();
  };

  const removeAccount = async (id) => {
    const { data } = await api.delete(`/accounts/${id}`);
    setAccounts((cur) => cur.filter((a) => a.id !== id));
    await fetchCooldowns();
    return data;
  };

  const refreshUser = async () => {
    const u = await fetchMe();
    if (u) {
      await Promise.all([fetchAccounts(), fetchCooldowns()]);
    }
    return u;
  };

  const markSubscriptionNoticeSeen = useCallback(async () => {
    try {
      await api.post('/auth/subscription-activation/seen');
    } catch (e) {
      // The notice is UI-only; do not block the dashboard if acknowledgement fails.
    } finally {
      setSubscriptionNotice(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user, accounts, cooldowns, subscriptionNotice, isAuthed, isAdmin, loading,
        login, register, logout,
        adminLoginStart, adminVerifyOtp, adminLogout,
        addAccount, removeAccount, bulkRemoveAccounts, refreshUser, markSubscriptionNoticeSeen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
